import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'
import { useEffect, useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Loader2, Plus, Trash2, Save, Languages, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { useEngineering } from '@/services/use-engineering'

export const Route = createFileRoute('/_authenticated/muhendislik/')({
    component: RouteComponent,
})

const topNav = [
    { title: 'Anasayfa', href: '/', isActive: false },
    { title: 'Mühendislik Yönetimi', href: '/muhendislik', isActive: true },
]

const localizedStringSchema = z.object({
    tr: z.string().min(1, 'Türkçe alan zorunludur'),
    en: z.string().optional(),
    fr: z.string().optional(),
    es: z.string().optional(),
    ru: z.string().optional(),
    nl: z.string().optional(),
    ar: z.string().optional(),
})

const engineeringSchema = z.object({
    badge: localizedStringSchema,
    title1: localizedStringSchema,
    title2: localizedStringSchema,
    body: localizedStringSchema,
    advancedTitle: localizedStringSchema,
    advancedBody: localizedStringSchema,
    features: z.array(z.object({
        icon: z.string(),
        text: localizedStringSchema,
    })),
    softwares: z.array(z.object({
        icon: z.string(),
        name: z.string(),
        details: localizedStringSchema,
    }))
})

type EngineeringFormValues = z.infer<typeof engineeringSchema>

const LANGUAGES = [
    { code: 'tr', label: 'Türkçe' },
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'es', label: 'Español' },
    { code: 'ru', label: 'Русский' },
    { code: 'nl', label: 'Nederlands' },
    { code: 'ar', label: 'العربية' },
]

function RouteComponent() {
    const { engineering, isLoading, updateEngineering, isUpdating } = useEngineering()
    const [isNormalSaving, setIsNormalSaving] = useState(false)

    const form = useForm<EngineeringFormValues>({
        resolver: zodResolver(engineeringSchema),
        defaultValues: {
            badge: { tr: '' },
            title1: { tr: '' },
            title2: { tr: '' },
            body: { tr: '' },
            advancedTitle: { tr: '' },
            advancedBody: { tr: '' },
            features: [],
            softwares: []
        },
    })

    const { fields: featureFields, append: appendFeature, remove: removeFeature } = useFieldArray({
        control: form.control,
        name: 'features',
    })

    const { fields: softwareFields, append: appendSoftware, remove: removeSoftware } = useFieldArray({
        control: form.control,
        name: 'softwares',
    })

    // Set default values after fetching
    useEffect(() => {
        if (engineering) {
            form.reset({
                badge: engineering.badge || { tr: '' },
                title1: engineering.title1 || { tr: '' },
                title2: engineering.title2 || { tr: '' },
                body: engineering.body || { tr: '' },
                advancedTitle: engineering.advancedTitle || { tr: '' },
                advancedBody: engineering.advancedBody || { tr: '' },
                features: engineering.features || [],
                softwares: engineering.softwares || [],
            })
        }
    }, [engineering, form])

    const onSubmit = async (values: EngineeringFormValues) => {
        setIsNormalSaving(true)
        try {
            await updateEngineering(values)
        } finally {
            setIsNormalSaving(false)
        }
    }

    const [isAISaving, setIsAISaving] = useState(false)
    const [showProgress, setShowProgress] = useState(false)
    const [progress, setProgress] = useState(0)
    const [currentStep, setCurrentStep] = useState('')

    const onTranslateAndSave = async () => {
        const values = form.getValues()
        setIsAISaving(true)
        setShowProgress(true)
        setProgress(10)
        setCurrentStep('AI Çeviri servisine bağlanılıyor...')

        const steps = [
            { p: 30, m: 'Başlıklar ve Metinler çeviriliyor...' },
            { p: 60, m: 'Özellikler (Features) çeviriliyor...' },
            { p: 85, m: 'Yazılımlar (Softwares) çeviriliyor...' },
            { p: 98, m: 'Tüm diller kaydediliyor, lütfen bekleyin...' },
        ]

        let currentStepIdx = 0
        const interval = setInterval(() => {
            if (currentStepIdx < steps.length) {
                setProgress(steps[currentStepIdx].p)
                setCurrentStep(steps[currentStepIdx].m)
                currentStepIdx++
            }
        }, 1200)

        try {
            // updateEngineering with translate: true flag
            await updateEngineering({ ...values, translate: true } as any)
            clearInterval(interval)
            setProgress(100)
            setCurrentStep('Mühendislik sayfası çevrildi ve kaydedildi!')
            setTimeout(() => setShowProgress(false), 2000)
        } catch (error) {
            clearInterval(interval)
            setShowProgress(false)
        } finally {
            setIsAISaving(false)
        }
    }

    if (isLoading) {
        return (
            <>
                <Header>
                    <TopNav links={topNav} />
                    <div className="ml-auto flex items-center gap-2">
                        <ThemeSwitch />
                        <ProfileDropdown />
                    </div>
                </Header>
                <Main>
                    <div className="flex h-[80vh] items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                </Main>
            </>
        )
    }

    return (
        <>
            <Header>
                <TopNav links={topNav} />
                <div className="ml-auto flex items-center gap-2">
                    <ThemeSwitch />
                    <ProfileDropdown />
                </div>
            </Header>

            <Main>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-2xl font-bold tracking-tight">Mühendislik & ARG-E</h1>
                                <p className="text-muted-foreground text-sm">Mühendislik sayfasının başlıklarını, tasarım araçlarını ve özelliklerini buradan yönetin.</p>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={onTranslateAndSave}
                                    disabled={isUpdating}
                                    className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
                                >
                                    {isAISaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Languages className="mr-2 h-4 w-4" />}
                                    AI ile Çevir ve Kaydet
                                </Button>
                                <Button type="submit" disabled={isUpdating}>
                                    {isNormalSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                                    Değişiklikleri Kaydet
                                </Button>
                            </div>
                        </div>

                        <Tabs defaultValue="hero" className="w-full">
                            <TabsList className="flex flex-wrap h-auto w-full">
                                <TabsTrigger value="hero" className="flex-1">Giriş Bölümü</TabsTrigger>
                                <TabsTrigger value="advanced" className="flex-1">Pazar & Ar-Ge</TabsTrigger>
                                <TabsTrigger value="features" className="flex-1">Özellikler (Features)</TabsTrigger>
                                <TabsTrigger value="softwares" className="flex-1">Yazılımlar</TabsTrigger>
                            </TabsList>

                            <TabsContent value="hero" className="space-y-4 pt-4">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Sol Taraf İçerikleri</CardTitle>
                                        <CardDescription>Sayfanın sol kısmında yer alan rozet, başlıklar ve ana açıklamayı düzenleyin.</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        <Tabs defaultValue="tr">
                                            <TabsList>
                                                {LANGUAGES.map(lang => (
                                                    <TabsTrigger key={lang.code} value={lang.code}>{lang.label}</TabsTrigger>
                                                ))}
                                            </TabsList>
                                            {LANGUAGES.map(lang => (
                                                <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                                                    <FormField
                                                        control={form.control}
                                                        name={`badge.${lang.code}` as any}
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <FormLabel>Rozet/Etiket ({lang.label})</FormLabel>
                                                                <FormControl>
                                                                    <Input {...field} placeholder="Örn: TASARIM & MÜHENDİSLİK" />
                                                                </FormControl>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <FormField
                                                            control={form.control}
                                                            name={`title1.${lang.code}` as any}
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <FormLabel>Kalın Başlık ({lang.label})</FormLabel>
                                                                    <FormControl>
                                                                        <Input {...field} placeholder="Örn: Fikirden" />
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                        <FormField
                                                            control={form.control}
                                                            name={`title2.${lang.code}` as any}
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <FormLabel>Alt/Renkli Başlık ({lang.label})</FormLabel>
                                                                    <FormControl>
                                                                        <Input {...field} placeholder="Örn: Mükemmelliğe" />
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                    </div>
                                                    <FormField
                                                        control={form.control}
                                                        name={`body.${lang.code}` as any}
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <FormLabel>Uzun Açıklama ({lang.label})</FormLabel>
                                                                <FormControl>
                                                                    <Textarea {...field} placeholder="Açıklama metni..." rows={3} />
                                                                </FormControl>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                </TabsContent>
                                            ))}
                                        </Tabs>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            <TabsContent value="advanced" className="space-y-4 pt-4">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Pazar & Ar-Ge Bölümü</CardTitle>
                                        <CardDescription>Sağ alt kısımdaki vurgulu Ar-Ge metnini (Örn: Zamanınız ve paranız cebinizde kalsın) düzenleyin.</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        <Tabs defaultValue="tr">
                                            <TabsList>
                                                {LANGUAGES.map(lang => (
                                                    <TabsTrigger key={lang.code} value={lang.code}>{lang.label}</TabsTrigger>
                                                ))}
                                            </TabsList>
                                            {LANGUAGES.map(lang => (
                                                <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                                                    <FormField
                                                        control={form.control}
                                                        name={`advancedTitle.${lang.code}` as any}
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <FormLabel>Başlık ({lang.label})</FormLabel>
                                                                <FormControl>
                                                                    <Input {...field} placeholder="Örn: PAZARDA REKABET" />
                                                                </FormControl>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                    <FormField
                                                        control={form.control}
                                                        name={`advancedBody.${lang.code}` as any}
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <FormLabel>Metin ({lang.label})</FormLabel>
                                                                <FormControl>
                                                                    <Textarea {...field} placeholder="Tüm süreçlerimizde inovasyonu..." rows={4} />
                                                                </FormControl>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                </TabsContent>
                                            ))}
                                        </Tabs>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            <TabsContent value="features" className="space-y-4 pt-4">
                                <Card>
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <CardTitle>Özellikler (Features)</CardTitle>
                                                <CardDescription>Mühendislik bölümündeki kısa özellikleri yönetin.</CardDescription>
                                            </div>
                                            <Button type="button" variant="outline" size="sm" onClick={() => appendFeature({ icon: 'Code2', text: { tr: '' } })}>
                                                <Plus className="mr-2 h-4 w-4" /> Ekle
                                            </Button>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        {featureFields.map((field, index) => (
                                            <Card key={field.id}>
                                                <CardHeader className="py-3 flex flex-row items-center justify-between bg-muted/50">
                                                    <span className="font-semibold text-sm">Özellik {index + 1}</span>
                                                    <Button type="button" variant="ghost" size="sm" className="h-8 text-destructive" onClick={() => removeFeature(index)}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </CardHeader>
                                                <CardContent className="pt-4 space-y-4">
                                                    <FormField
                                                        control={form.control}
                                                        name={`features.${index}.icon`}
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <FormLabel>İkon (Lucide icon name)</FormLabel>
                                                                <FormControl>
                                                                    <Input {...field} placeholder="Örn: Code2, Layers, Monitor" />
                                                                </FormControl>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                    <Tabs defaultValue="tr">
                                                        <TabsList className="h-8">
                                                            {LANGUAGES.map(lang => (
                                                                <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.code.toUpperCase()}</TabsTrigger>
                                                            ))}
                                                        </TabsList>
                                                        {LANGUAGES.map(lang => (
                                                            <TabsContent key={lang.code} value={lang.code} className="pt-2">
                                                                <FormField
                                                                    control={form.control}
                                                                    name={`features.${index}.text.${lang.code}` as any}
                                                                    render={({ field }) => (
                                                                        <FormItem>
                                                                            <FormLabel className="sr-only">Metin ({lang.code})</FormLabel>
                                                                            <FormControl>
                                                                                <Input {...field} placeholder={`Özellik metni (${lang.label})`} />
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </TabsContent>
                                                        ))}
                                                    </Tabs>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            <TabsContent value="softwares" className="space-y-4 pt-4">
                                <Card>
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <CardTitle>Yazılımlar (Softwares)</CardTitle>
                                                <CardDescription>Kullanılan mühendislik yazılımlarını yönetin.</CardDescription>
                                            </div>
                                            <Button type="button" variant="outline" size="sm" onClick={() => appendSoftware({ icon: 'Monitor', name: '', details: { tr: '' } })}>
                                                <Plus className="mr-2 h-4 w-4" /> Ekle
                                            </Button>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        {softwareFields.map((field, index) => (
                                            <Card key={field.id}>
                                                <CardHeader className="py-3 flex flex-row items-center justify-between bg-muted/50">
                                                    <span className="font-semibold text-sm">Yazılım {index + 1}</span>
                                                    <Button type="button" variant="ghost" size="sm" className="h-8 text-destructive" onClick={() => removeSoftware(index)}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </CardHeader>
                                                <CardContent className="pt-4 space-y-4">
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <FormField
                                                            control={form.control}
                                                            name={`softwares.${index}.name`}
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <FormLabel>Yazılım Adı</FormLabel>
                                                                    <FormControl>
                                                                        <Input {...field} placeholder="Örn: AutoCAD" />
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                        <FormField
                                                            control={form.control}
                                                            name={`softwares.${index}.icon`}
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <FormLabel>İkon (Lucide)</FormLabel>
                                                                    <FormControl>
                                                                        <Input {...field} placeholder="Örn: Monitor, Cpu" />
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                    </div>

                                                    <Tabs defaultValue="tr">
                                                        <TabsList className="h-8">
                                                            {LANGUAGES.map(lang => (
                                                                <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.code.toUpperCase()}</TabsTrigger>
                                                            ))}
                                                        </TabsList>
                                                        {LANGUAGES.map(lang => (
                                                            <TabsContent key={lang.code} value={lang.code} className="pt-2">
                                                                <FormField
                                                                    control={form.control}
                                                                    name={`softwares.${index}.details.${lang.code}` as any}
                                                                    render={({ field }) => (
                                                                        <FormItem>
                                                                            <FormLabel className="sr-only">Detay ({lang.code})</FormLabel>
                                                                            <FormControl>
                                                                                <Textarea {...field} placeholder={`Yazılım açıklaması (${lang.label})`} rows={2} />
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </TabsContent>
                                                        ))}
                                                    </Tabs>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        </Tabs>
                    </form>
                </Form>

                <Dialog open={showProgress} onOpenChange={setShowProgress}>
                    <DialogContent className="sm:max-w-md [&>button]:hidden">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                {progress === 100 ? (
                                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                                ) : (
                                    <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                                )}
                                AI Çeviri İşlemi
                            </DialogTitle>
                            <DialogDescription>
                                İçerikleriniz diğer dillere çevriliyor. Lütfen sayfayı kapatmayın.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-6 py-4">
                            <Progress value={progress} className="h-2" />
                            <p className="text-sm font-medium text-center text-muted-foreground animate-pulse">
                                {currentStep}
                            </p>
                        </div>
                    </DialogContent>
                </Dialog>
            </Main>
        </>
    )
}
